import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StepHeader, OnboardingButton, OnboardingInput } from '../components'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { ArrowRightIcon } from '@untitledui-icons/react/line'
import { parseGoogleAddress } from '@/lib/addressParser'

interface BusinessDetailsStepProps {
  onNext: () => void
  onBack: () => void
}

interface FormData {
  businessName: string
  businessGroupName: string
  addressLine1: string
  addressLine2: string
  postcode: string
  city: string
  website: string
  phone: string
  useSamePhoneForEvents: boolean
}

export function BusinessDetailsStep({ onNext, onBack }: BusinessDetailsStepProps) {
  const { t } = useTranslation()
  const { placeDetails, setPlaceDetails, businessType, setBookingDetectionStarted } = useOnboardingStore()

  // Parse address into components using shared parser (supports NL, ES, DE, FR, UK, etc.)
  const parsedAddress = placeDetails?.address
    ? parseGoogleAddress(placeDetails.address)
    : { street: '', postcode: '', city: '' }

  const [formData, setFormData] = useState<FormData>({
    businessName: placeDetails?.name || '',
    businessGroupName: '',
    addressLine1: parsedAddress.street,
    addressLine2: '',
    postcode: parsedAddress.postcode,
    city: parsedAddress.city,
    website: placeDetails?.website || '',
    phone: placeDetails?.phone || '',
    useSamePhoneForEvents: false,
  })

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({})

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const handleContinue = () => {
    // Validate required fields
    const newErrors: Partial<Record<keyof FormData, string>> = {}
    if (!formData.businessName.trim()) newErrors.businessName = t('resources.signup.business_details.error_required', 'This field is required')
    if (!formData.addressLine1.trim()) newErrors.addressLine1 = t('resources.signup.business_details.error_required', 'This field is required')
    if (!formData.city.trim()) newErrors.city = t('resources.signup.business_details.error_required', 'This field is required')
    if (!formData.phone.trim()) newErrors.phone = t('resources.signup.business_details.error_required', 'This field is required')

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    // Save updated details to store
    setPlaceDetails({
      ...placeDetails,
      name: formData.businessName,
      address: `${formData.addressLine1}${formData.addressLine2 ? `, ${formData.addressLine2}` : ''}, ${formData.postcode} ${formData.city}`,
      phone: formData.phone,
      website: formData.website,
      google_place_id: placeDetails?.google_place_id || '',
      useSamePhoneForEvents: formData.useSamePhoneForEvents,
    })

    // Start booking detection for restaurants with a website
    if (businessType === 'RESTAURANT' && formData.website) {
      setBookingDetectionStarted(true)
    }

    onNext()
  }

  return (
    <div className="flex w-full flex-1 flex-col">
      <StepHeader title={t('resources.signup.business_details.title')} onBack={onBack} />

      {/* Form fields + checkbox */}
      <div className="flex flex-col gap-3">
        {/* Input fields */}
        <div className="flex flex-col gap-4">
          <OnboardingInput
            label={t('resources.signup.business_details.business_name')}
            value={formData.businessName}
            onChange={(e) => handleChange('businessName', e.target.value)}
            error={errors.businessName}
          />

          <OnboardingInput
            label={t('resources.signup.business_details.business_group_name')}
            value={formData.businessGroupName}
            onChange={(e) => handleChange('businessGroupName', e.target.value)}
          />

          <OnboardingInput
            label={t('resources.signup.business_details.address_line_1')}
            value={formData.addressLine1}
            onChange={(e) => handleChange('addressLine1', e.target.value)}
            error={errors.addressLine1}
          />

          <OnboardingInput
            label={t('resources.signup.business_details.address_line_2')}
            value={formData.addressLine2}
            onChange={(e) => handleChange('addressLine2', e.target.value)}
          />

          {/* Postcode + City row */}
          <div className="flex gap-4">
            <OnboardingInput
              label={t('resources.signup.business_details.postcode')}
              value={formData.postcode}
              onChange={(e) => handleChange('postcode', e.target.value)}
            />
            <OnboardingInput
              label={t('resources.signup.business_details.city')}
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              error={errors.city}
            />
          </div>

          <OnboardingInput
            label={t('resources.signup.business_details.website')}
            value={formData.website}
            onChange={(e) => handleChange('website', e.target.value)}
          />

          <OnboardingInput
            label={t('resources.signup.business_details.phone')}
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            error={errors.phone}
          />
        </div>

        {/* Checkbox - same phone for events */}
        <label className="flex cursor-pointer items-start gap-2">
          <div className="flex shrink-0 items-center justify-center pt-[2px]">
            <input
              type="checkbox"
              checked={formData.useSamePhoneForEvents}
              onChange={(e) => handleChange('useSamePhoneForEvents', e.target.checked)}
              className="h-4 w-4 cursor-pointer rounded-[4px] border border-[#d5d7da] text-[#181d27] focus:ring-[#181d27]"
            />
          </div>
          <span className="flex-1 text-sm font-medium leading-5 text-[#414651]">
            {t('resources.signup.business_details.same_phone_checkbox')}
          </span>
        </label>
      </div>

      {/* Continue button */}
      <div className="mt-auto pb-8 pt-6 lg:mt-8 lg:pt-0">
        <OnboardingButton
          onClick={handleContinue}
          iconTrailing={ArrowRightIcon}
        >
          {t('resources.signup.common.continue')}
        </OnboardingButton>
      </div>
    </div>
  )
}

